import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { extractFieldCodes } from 'helpers/document-upload-helpers';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { ExtractedFieldsDisplay } from 'components/ExtractedFieldsDisplay/ExtractedFieldsDisplay';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';

import { selectCandidateApplication, updateCandidateValues } from 'api/candidate-application-slice';

import { DocumentDualUpload } from './DocumentDualUpload';

export const DocumentUploadWithFields = ({
  documentTitle = 'Select your files to upload',
  frontTitle = 'Front',
  backTitle = 'Back',
  uploadCode,
  selfieId,
  selfieIdAtLastVerify = null,
  reTakeSelfie = false,
  frontValue,
  backValue,
  frontAttempts = 0,
  backAttempts = 0,
  backConditions = null,
  modelParams = {},
  listCode = '',
  metadata = {},
  className,
  bucketFolder,
  maxSize = 20971520,
  accept,
  allowedTypesMode,
  idType,
  brand,
  useModel = false,
  disabled = false,
  verifyDocument = false,
  expiryTolerance = null,
  uploadStatus = '',
  uploadError = '',
  faceSimilarity = null,
  faceSimilarityStatus = null,
  hasPhoto = null,
  documentDescription = '',
  additionalInstructions = '',
  countryCode = '',
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  displayFields = [],
  idValues = {},
  uploadId = null,
  confirmedByCand = false,
  handleStateOnVerify = () => {},
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  updateUpload = async () => {},
  deleteUpload = async () => true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { candidateFields } = useSelector(selectCandidateApplication);
  const brandingStyleVariables = useBrandingStyleVariables(brand);

  const [dualState, setDualState] = useState({
    outcome: 'idle',
    extractedFields: null,
    frontUploadId: null,
    backUploadId: null,
    busy: false,
  });
  const [isConfirmed, setIsConfirmed] = useState(confirmedByCand);

  // Only react to confirmedByCand going true -> false as a genuine "un-confirm" (e.g. its
  // paired back upload was deleted, invalidating the prior confirmation) — not the very first
  // render, where it starts false simply because the async confirm dispatch from handleConfirm
  // hasn't landed yet. Without gating on the previous value, that landing-lag window would
  // immediately stomp the optimistic setIsConfirmed(true) below back to false.
  const previousConfirmedByCandRef = useRef(confirmedByCand);
  useEffect(() => {
    const wasConfirmed = previousConfirmedByCandRef.current;
    previousConfirmedByCandRef.current = confirmedByCand;
    if (confirmedByCand) {
      setIsConfirmed(true);
    } else if (wasConfirmed) {
      setIsConfirmed(false);
    }
  }, [confirmedByCand]);

  // Hidden while busy (a fresh upload or a re-verify in flight) — the currently-held outcome
  // is a leftover from the PREVIOUS result and would otherwise keep showing as if it already
  // reflected whatever's being uploaded/verified right now.
  const showExtractedFields =
    !dualState.busy &&
    displayFields?.length > 0 &&
    (dualState.outcome === 'verified' || dualState.outcome === 'forceAccepted');

  // Prefer live verify extracted_fields over Redux idValues (can lag a render or two).
  const effectiveIdValues = dualState.extractedFields ?? idValues;

  // Clear confirmed candidateValues when frontUploadId changes mid-session (incl. via null).
  // A real baseline must be a non-null id before any change counts as a "swap" — on mount,
  // dualState.frontUploadId can start at null and only pick up its real value a render later
  // once the candidate's uploads have loaded, which is hydration finishing, not a document
  // being replaced. Without hasEstablishedBaselineRef, that null -> real-id transition looked
  // identical to a genuine swap and wrongly reset isConfirmed back to false the moment the
  // real confirmed_by_cand value arrived, showing "action required" for an already-confirmed
  // document. Once a real id has been seen, every subsequent change (including to/from null
  // for an actual delete-then-reupload) is still treated as a genuine swap as before.
  // Also cleared on a back-only change — a re-verify triggered by replacing just the back can
  // still change extracted fields (e.g. the expiry date reads off the back), so a value the
  // candidate already confirmed against the OLD back must not keep overriding the new
  // extraction. Front and back baselines are established together, gated on the front (the
  // back can legitimately start out null in CONDITIONAL mode before discovery ever runs).
  const previousFrontUploadIdRef = useRef(undefined);
  const previousBackUploadIdRef = useRef(undefined);
  const hasEstablishedBaselineRef = useRef(false);
  useEffect(() => {
    const currentFrontId = dualState.frontUploadId;
    const currentBackId = dualState.backUploadId;
    if (!hasEstablishedBaselineRef.current) {
      previousFrontUploadIdRef.current = currentFrontId;
      previousBackUploadIdRef.current = currentBackId;
      if (currentFrontId != null) {
        hasEstablishedBaselineRef.current = true;
      }
      return;
    }
    const frontChanged = currentFrontId !== previousFrontUploadIdRef.current;
    const backChanged = currentBackId !== previousBackUploadIdRef.current;
    if (!frontChanged && !backChanged) return;
    previousFrontUploadIdRef.current = currentFrontId;
    previousBackUploadIdRef.current = currentBackId;

    const codes = extractFieldCodes(displayFields);
    if (!codes.length) return;
    const filteredCandidateValues = { ...candidateFields.candidateValues };
    let changed = false;
    codes.forEach(code => {
      if (code in filteredCandidateValues) {
        delete filteredCandidateValues[code];
        changed = true;
      }
    });
    if (changed) {
      dispatch(updateCandidateValues({ updatedCandidateValues: filteredCandidateValues }));
    }
    setIsConfirmed(false);
  }, [dualState.frontUploadId, dualState.backUploadId, displayFields, candidateFields, dispatch]);

  const handleConfirm = () => {
    setIsConfirmed(true);
  };

  return (
    <div
      className={cn('w-full flex flex-col', { [className]: className, 'u-disabled': disabled })}
      style={brandingStyleVariables}>
      <div className="flex flex-col gap-4">
        <DocumentDualUpload
          documentTitle={documentTitle}
          documentDescription={documentDescription}
          additionalInstructions={additionalInstructions}
          countryCode={countryCode}
          frontTitle={frontTitle}
          backTitle={backTitle}
          uploadCode={uploadCode}
          selfieId={selfieId}
          selfieIdAtLastVerify={selfieIdAtLastVerify}
          reTakeSelfie={reTakeSelfie}
          frontValue={frontValue}
          backValue={backValue}
          frontAttempts={frontAttempts}
          backAttempts={backAttempts}
          backConditions={backConditions}
          idValues={idValues}
          modelParams={modelParams}
          listCode={listCode}
          metadata={metadata}
          bucketFolder={bucketFolder}
          maxSize={maxSize}
          accept={accept}
          allowedTypesMode={allowedTypesMode}
          idType={idType}
          brand={brand}
          useModel={useModel}
          disabled={disabled}
          verifyDocument={verifyDocument}
          expiryTolerance={expiryTolerance}
          uploadStatus={uploadStatus}
          uploadError={uploadError}
          faceSimilarity={faceSimilarity}
          faceSimilarityStatus={faceSimilarityStatus}
          showTitleInUploadSlot={false}
          hasPhoto={hasPhoto}
          handleStateOnVerify={handleStateOnVerify}
          onChange={onChange}
          onStateChange={setDualState}
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          updateUpload={updateUpload}
          deleteUpload={deleteUpload}
        />
      </div>

      {needsComplementaryDoc && complementaryInstructions && (
        <DisplayBox show={true} alignLeft>
          <span className="u-font-size-small u-font-weight-400">{complementaryInstructions}</span>
        </DisplayBox>
      )}

      {showExtractedFields && !disabled && (
        <div className="d-flex flex-column u-gap-medium">
          {!isConfirmed && (
            <FormMessage
              message={t('components.documentUploadWithFields.actionRequiredMessage')}
              type={MessageType.Warning}
              noMargin
              closeable={false}
            />
          )}
          <ExtractedFieldsDisplay
            // Remount on a new front OR back upload — ExtractedFieldsDisplay only seeds its form
            // once per mount, so without the back id here, replacing only the back (which can
            // still change extracted fields via a fresh re-verify, e.g. the expiry date) would
            // leave this showing the previous verify's stale values forever.
            key={`${dualState.frontUploadId ?? uploadId}:${dualState.backUploadId ?? ''}`}
            displayFields={displayFields}
            idValues={effectiveIdValues}
            brand={brand}
            uploadId={uploadId ?? dualState.frontUploadId}
            documentTitle={documentTitle}
            confirmedByCand={isConfirmed}
            onConfirm={handleConfirm}
          />
        </div>
      )}
    </div>
  );
};

DocumentUploadWithFields.propTypes = {
  documentTitle: PropTypes.string,
  frontTitle: PropTypes.string,
  backTitle: PropTypes.string,
  uploadCode: PropTypes.any,
  selfieId: PropTypes.number,
  selfieIdAtLastVerify: PropTypes.number,
  reTakeSelfie: PropTypes.bool,
  frontValue: PropTypes.any,
  backValue: PropTypes.any,
  frontAttempts: PropTypes.number,
  backAttempts: PropTypes.number,
  backConditions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  modelParams: PropTypes.object,
  listCode: PropTypes.string,
  metadata: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  allowedTypesMode: PropTypes.string,
  idType: PropTypes.string,
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  disabled: PropTypes.bool,
  verifyDocument: PropTypes.bool,
  expiryTolerance: PropTypes.number,
  uploadStatus: PropTypes.string,
  uploadError: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  faceSimilarity: PropTypes.number,
  faceSimilarityStatus: PropTypes.string,
  hasPhoto: PropTypes.bool,
  needsComplementaryDoc: PropTypes.bool,
  complementaryInstructions: PropTypes.string,
  displayFields: PropTypes.array,
  idValues: PropTypes.object,
  uploadId: PropTypes.number,
  confirmedByCand: PropTypes.bool,
  handleStateOnVerify: PropTypes.func,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  updateUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
};
